// AI-observation stash — localStorage-backed.
//
// Captures AI Suggest / AI Score suggestions at suggest-time, keys them
// to a ball_id at save-time, and fires the /api/ai-learning/observe/
// correction POST when an operator later edits the ball through
// EditBallDialog (from any page — BBP, EditBalls.jsx, etc.).
//
// Why localStorage: the previous in-memory useRef stash in BBP was lost
// on refresh AND inaccessible from other surfaces. Persisting here makes
// the correction signal survive both.
//
// Storage shape:
//   { pending: Entry | null,
//     byBallId: { [ball_id]: Entry } }   // capped 200 FIFO
//
// Entry: { feature_code, context, ai_output, model_name, t }
//   feature_code MUST match ai_learning.models.FeatureCode (lowercase).
//   t = Date.now(), used for FIFO eviction.

import api from "../api/axios";
import { getBallForEdit } from "../api/ballbyball.api";

const KEY = "zcd_ai_obs_stash_v1";
const MAX_ENTRIES = 200;

function read() {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return { pending: null, byBallId: {} };
    const parsed = JSON.parse(raw);
    return {
      pending: parsed?.pending ?? null,
      byBallId: parsed?.byBallId ?? {},
    };
  } catch {
    return { pending: null, byBallId: {} };
  }
}

function write(state) {
  try {
    localStorage.setItem(KEY, JSON.stringify(state));
  } catch {
    // Quota / private mode — drop silently. Auto-train is best-effort.
  }
}

export function setPending(entry) {
  if (!entry || !entry.feature_code) return;
  const state = read();
  state.pending = { ...entry, t: Date.now() };
  write(state);
}

export function clearPending() {
  const state = read();
  if (state.pending) {
    state.pending = null;
    write(state);
  }
}

export function commitForBall(ballId) {
  if (!ballId) return;
  const state = read();
  if (!state.pending) return;
  state.byBallId[ballId] = state.pending;
  state.pending = null;
  // FIFO evict: keep the 200 most-recent by `t`.
  const ids = Object.keys(state.byBallId);
  if (ids.length > MAX_ENTRIES) {
    const sorted = ids
      .map((k) => [k, state.byBallId[k]?.t || 0])
      .sort((a, b) => a[1] - b[1]);
    const dropCount = ids.length - MAX_ENTRIES;
    for (let i = 0; i < dropCount; i += 1) {
      delete state.byBallId[sorted[i][0]];
    }
  }
  write(state);
}

export function getForBall(ballId) {
  if (!ballId) return null;
  return read().byBallId[ballId] || null;
}

// Fire-and-forget. Reads the saved ball, computes accepted_output by
// projecting the stash's ai_output keys onto the fresh ball state, then
// POSTs to /api/ai-learning/observe/. Auto-train logging is best-effort
// — any failure is swallowed so it can't block the scoring UI.
//
// Does NOT delete the stash entry — repeat edits to the same ball
// should each record their own observation row (the backend is
// append-only by design, capturing the operator's edit arc).
export async function fireObserveOnSave({ ballId, matchId }) {
  if (!ballId) return;
  const stash = getForBall(ballId);
  if (!stash) return;
  try {
    const freshBall = await getBallForEdit(ballId)
      .then((r) => r.data)
      .catch(() => null);
    if (!freshBall) return;
    const accepted = {};
    Object.keys(stash.ai_output || {}).forEach((k) => {
      if (freshBall?.[k] !== undefined) accepted[k] = freshBall[k];
      else if (freshBall?.analytics?.[k] !== undefined) accepted[k] = freshBall.analytics[k];
      else if (freshBall?.spatial?.[k] !== undefined) accepted[k] = freshBall.spatial[k];
    });
    await api.post("/api/ai-learning/observe/", {
      feature_code: stash.feature_code,
      context: stash.context || {},
      ai_output: stash.ai_output || {},
      accepted_output: accepted,
      match_id: matchId,
      ball_id: ballId,
      model_name: stash.model_name || "",
    }).catch(() => {});
  } catch {
    // Swallow — auto-train logging must never break the caller.
  }
}
